import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, tap, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { AppState } from './index';
import { TenantsDBService } from '../services/tenants-db.service';
import { TenantService } from '../services/tenant.service';
import { StripeConnectionService } from '../services/stripe-connection.service';
import { throwError } from 'rxjs';
import { PlatformActions } from './action-types';
import { emailProviderSettingsLoaded } from './platform.actions';
import { selectEmailProviderSettings, selectTenantInfo } from './selectors';
import { MessagesService } from '../shared/services/messages.service';
import { LoadingService } from '../shared/services/loading.service';
import { PlatformPromoService } from '../services/platform-promo.service';
import { StudentEnrollmentSettings } from "../models/student-enrollment-settings.model";
import { SnackBarService } from '../shared/services/snack-bar.service';


@Injectable()
export class PlatformEffects {
  private actions$ = inject(Actions);
  private tenant = inject(TenantService);
  private store = inject<Store<AppState>>(Store);
  private loading = inject(LoadingService);
  private tenantsDB = inject(TenantsDBService);
  private stripeConnectionService = inject(StripeConnectionService);
  private messages = inject(MessagesService);
  private promo = inject(PlatformPromoService);

  private snackBarService = inject(SnackBarService);

  saveTheme$ = createEffect(() => this.actions$
    .pipe(
      ofType(PlatformActions.saveTheme),
      concatMap(theme => this.tenantsDB.saveTheme(this.tenant.id, theme.primaryColor, theme.accentColor, theme.salesPageBannerColor, theme.salesPageTitleIconColor, theme.salesPageListItemIconColor)),
      catchError(err => {
        this.messages.error('Could not save theme colors.');
        return throwError(err);
      })
    ),
    { dispatch: false });

  saveNewsletter$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(PlatformActions.saveNewsletterFormContent),
        concatMap(action => this.loading.showLoaderUntilCompleted(
          this.tenantsDB.updateTenant(this.tenant.id, { newsletter: action.newsletter })))
      ), { dispatch: false }
  );

  saveSchoolSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlatformActions.updateSchoolDetails),  // Listens for this action
      concatMap(action =>
        this.loading.showLoaderUntilCompleted(
          this.tenantsDB.updateTenant(this.tenant.id, action.changes)  // Update tenant in DB
            .pipe(
              tap(() => {
                const newLangCode = action.changes?.aiStudentAssistantSetting?.studentAssistantLanguage?.code;
                const oldLangCode = action?.previousLanguageCode;
                // Show messages based on the type of update
                if (action?.changes?.brandTheme) {
                  this.messages.info(`The new brand is now applied.`);
                } else if (action?.changes?.emailBrandingConfig) {

                  this.messages.info(`The email template configuration has been updated.`);
                } else if (action?.changes?.transactionalEmailSettings) {
                  this.messages.info(`Default form address has been updated.`);
                } else if (oldLangCode && newLangCode && oldLangCode !== newLangCode) {
                  this.snackBarService.i18nMessage("AI Student Assistant language updated successfully!");
                } else if (action?.changes?.aiCreatorAssistantLanguage) {
                  this.snackBarService.i18nMessage("AI Creator Assistant language updated successfully!");
                }
                else {
                  // this.messages.success("Successfully saved.");
                }
              })
            )
        )
      ),
    ),
    { dispatch: false }
  );

  loadEmailProviderSettings$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(PlatformActions.loadEmailProviderSettings),
        concatMap(() => this.tenantsDB.loadTenantPrivateSettings()),
        map(settings => emailProviderSettingsLoaded({ emailProviderSettings: settings?.emailProvider }))
      ));

  saveEmailProviderSettings$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(PlatformActions.activateEmailMarketingIntegration),
        concatMap(action => this.tenantsDB.activateEmailMarketingIntegration(action?.emailProviderSettings))
      ),
    { dispatch: false });


  cancelEmailProviderIntegration$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(PlatformActions.cancelEmailMarketingIntegration),
        withLatestFrom(this.store.pipe(select(selectEmailProviderSettings))),
        concatMap(([action, settings]) => this.tenantsDB.cancelEmailMarketingIntegration()
        )
      ), { dispatch: false });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  // updateStudentEnrollmentSettings$ = createEffect(() =>
  // this.actions$
  //   .pipe(
  //     ofType(PlatformActions.updateStudentEnrollmentSettings),
  //     withLatestFrom(this.store.pipe(select(selectTenantInfo))),
  //     concatMap(([action, tenant ]) =>
  //       this.loading.showLoaderUntilCompleted(
  //         this.tenantsDB.updateStudentEnrollmentSettings(this.tenant.id,
  //           action.changes,
  //           tenant
  //         )
  //           .pipe(
  //             tap(() => {
  //               this.messages.success("Updated Student Enrollment Settings")
  //             }),
  //             catchError((err) => {
  //               this.messages.error(err?.toString());
  //               return throwError(err);
  //             })
  //           )
  //       )
  //     ),
  //   ), {dispatch: false});

  constructor() {

  }


}
